import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import { cn } from '@/lib/utils';
import { kr, signedKr } from '../../lib/money';
import { formatShortDate } from '../../lib/period';
import type { RouterOutputs } from '../../lib/trpc';
import { Meter } from '../common/Panel';
import { Skeleton } from '../ui/skeleton';

type Summary = RouterOutputs['period']['summary'];
type Balance = NonNullable<Summary['balance']>;

const PACE = {
  under: { label: 'Under takten', className: 'bg-positive-soft text-positive' },
  // biome-ignore lint/style/useNamingConvention: mirrors the API's paceStatus value
  on_track: { label: 'I fas', className: 'bg-positive-soft text-positive' },
  over: { label: 'Över takten', className: 'bg-warning-soft text-warning' },
} as const;

const HERO_NUMBER =
  'mt-1 text-[44px] leading-none font-semibold tracking-tight tabular-nums sm:text-5xl';

/**
 * The one number for the day. In the current period it's led by the money
 * actually in the accounts (bills still due set aside); other periods show
 * the income-based result.
 */
export function LeftToSpendCard({ summary }: { summary: Summary | undefined }) {
  if (!summary) {
    return (
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-56" />
        <Skeleton className="h-2 w-full" />
      </section>
    );
  }
  return summary.balance ? (
    <BalanceHero summary={summary} balance={summary.balance} />
  ) : (
    <IncomeHero summary={summary} />
  );
}

/** Current period: what's on the accounts minus what still has to go out */
function BalanceHero({ summary, balance }: { summary: Summary; balance: Balance }) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const { period } = summary;
  const negative = balance.freeToSpend < 0;
  const missing = balance.accounts.filter((a) => a.balance == null).map((a) => a.name);
  const planLink = { to: '/plan', search: { year: period.year, month: period.month } } as const;

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <p className="text-sm font-medium text-muted-foreground">Kvar att spendera</p>

      <p className={cn(HERO_NUMBER, negative && 'text-negative')}>{kr(balance.freeToSpend)}</p>

      <p className="mt-2 text-sm text-muted-foreground">
        {negative ? (
          <span className="text-negative">
            Räkningarna som är kvar är mer än det som finns på kontot.
          </span>
        ) : (
          balance.perDay != null && (
            <>
              <span className="font-semibold text-foreground tabular-nums">
                {kr(balance.perDay)}
              </span>{' '}
              per dag <DaysLeft period={period} />
            </>
          )
        )}
      </p>

      <div className="mt-4 rounded-xl bg-muted/60">
        <div className="grid grid-cols-3 divide-x divide-border text-center">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((o) => !o)}
            className="rounded-l-xl px-1 py-2.5 transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
          >
            <span className="flex items-center justify-center gap-0.5 text-[11px] text-muted-foreground">
              På kontot
              <ChevronDown
                aria-hidden
                className={cn('size-3 transition-transform', open && 'rotate-180')}
              />
            </span>
            <span className="block text-sm font-semibold tabular-nums">
              {kr(balance.total - balance.reservedPending)}
            </span>
          </button>
          <Link {...planLink} className="px-1 py-2.5 transition-colors hover:bg-muted">
            <Stat label="Räkningar kvar" value={kr(balance.reservedFixed)} />
          </Link>
          <Link to="/savings" className="rounded-r-xl px-1 py-2.5 transition-colors hover:bg-muted">
            <Stat label="Sparat" value={kr(summary.savings.actual)} />
          </Link>
        </div>
        {open && (
          <ul id={listId} className="border-t border-border px-3 py-1 text-sm">
            {balance.accounts.map((a) => (
              <li
                key={a.accountId}
                className="flex items-baseline gap-2 border-b border-border/60 py-2 last:border-0"
              >
                <span className="min-w-0 flex-1 truncate">{a.name}</span>
                {a.balance == null ? (
                  <span className="text-xs text-muted-foreground">saknar saldo</span>
                ) : (
                  <>
                    {a.asOfDate && (
                      <span className="text-xs text-muted-foreground">
                        {formatShortDate(a.asOfDate)}
                      </span>
                    )}
                    <span className="tabular-nums">{kr(a.balance)}</span>
                  </>
                )}
              </li>
            ))}
            {balance.reservedPending > 0 && (
              <li className="flex items-baseline gap-2 py-2">
                <span className="min-w-0 flex-1 truncate">Reserverade kortköp</span>
                <span className="text-xs text-muted-foreground">ej bokförda</span>
                <span className="tabular-nums">{signedKr(-balance.reservedPending)}</span>
              </li>
            )}
          </ul>
        )}
      </div>

      {balance.asOf && (
        <p className="mt-3 text-xs text-muted-foreground">
          Saldo {formatShortDate(balance.asOf)}
          {missing.length > 0 && ` · saknar saldo för ${missing.join(', ')}`}
        </p>
      )}

      <PeriodResult summary={summary} />
    </section>
  );
}

/**
 * How much of this period's income has gone (fixed costs, purchases,
 * savings), next to how far into the period we are. Unlike the balance
 * above, money carried over from earlier periods doesn't count here.
 */
function PeriodResult({ summary }: { summary: Summary }) {
  const { period, income } = summary;
  const result = summary.leftToSpend;
  const used = income.planned - result;
  const usedShare = income.planned > 0 ? used / income.planned : 0;
  const timeShare = period.daysTotal > 0 ? period.daysElapsed / period.daysTotal : 0;

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">Periodens resultat</span>
        <span className={cn('text-sm font-semibold tabular-nums', result < 0 && 'text-negative')}>
          {signedKr(result)}
        </span>
      </div>
      {income.planned > 0 ? (
        <>
          <Meter
            className="mt-2"
            value={usedShare}
            marker={timeShare}
            tone={usedShare > 1 ? 'negative' : usedShare > timeShare ? 'warning' : 'primary'}
            label={`${kr(used)} av periodens inkomst ${kr(income.planned)} har gått åt. ${Math.round(timeShare * 100)} % av perioden har gått.`}
          />
          <p className="mt-1.5 flex justify-between gap-3 text-xs text-muted-foreground tabular-nums">
            <span>
              {kr(used)} av {kr(income.planned)} inkomst har gått åt
            </span>
            <span className="shrink-0">{Math.round(usedShare * 100)} %</span>
          </p>
        </>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          Ingen inkomst har kommit in den här perioden än.
        </p>
      )}
    </div>
  );
}

/** Past/future periods (or no saldo data): the income-based result */
function IncomeHero({ summary }: { summary: Summary }) {
  const { period, variable, fixedCosts, income, savings } = summary;
  // Only arrived income counts, so a period starts negative until salary lands
  const awaitingIncome = income.planned === 0;
  const negative = summary.leftToSpend < 0 && !awaitingIncome;
  const pace = PACE[variable.paceStatus];
  const spentShare = variable.budget > 0 ? variable.spent / variable.budget : 0;
  const markerShare = variable.budget > 0 ? variable.expectedByNow / variable.budget : 0;

  const heading = period.isPast
    ? 'Blev över'
    : period.isFuture
      ? 'Planerat att spendera'
      : 'Kvar att spendera';

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{heading}</p>
        {period.isCurrent && !awaitingIncome && (
          <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', pace.className)}>
            {pace.label}
          </span>
        )}
      </div>

      <p className={cn(HERO_NUMBER, negative && 'text-negative')}>{kr(summary.leftToSpend)}</p>

      <p className="mt-2 text-sm text-muted-foreground">
        {awaitingIncome ? (
          'Lönen har inte kommit in än – siffran räknar bara pengar som har kommit.'
        ) : negative ? (
          <span className="text-negative">Mer har gått ut än vad som kommit in.</span>
        ) : period.isCurrent && summary.perDay != null ? (
          <>
            <span className="font-semibold text-foreground tabular-nums">{kr(summary.perDay)}</span>{' '}
            per dag <DaysLeft period={period} />
          </>
        ) : (
          `${kr(variable.spent)} gick till rörliga köp`
        )}
      </p>

      {variable.budget > 0 && (
        <div className="mt-4 space-y-1.5">
          <Meter
            value={spentShare}
            marker={period.isCurrent ? markerShare : undefined}
            tone={
              spentShare > 1 ? 'negative' : variable.paceStatus === 'over' ? 'warning' : 'primary'
            }
            label={`Rörliga köp ${kr(variable.spent)} av ${kr(variable.budget)}. Enligt takten ${kr(variable.expectedByNow)} hittills.`}
          />
          <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
            <span>
              {kr(variable.spent)} av {kr(variable.budget)} spenderat
            </span>
            {period.isCurrent && <span>takt {kr(variable.expectedByNow)}</span>}
          </div>
        </div>
      )}

      <Link
        to="/plan"
        search={{ year: period.year, month: period.month }}
        className="mt-4 grid grid-cols-3 divide-x divide-border rounded-xl bg-muted/60 py-2.5 text-center transition-colors hover:bg-muted"
      >
        <Stat label="Inkomst" value={kr(income.planned)} />
        <Stat label="Fasta" value={kr(fixedCosts.total)} />
        <Stat label="Sparat" value={kr(savings.actual)} />
      </Link>
    </section>
  );
}

/**
 * "i 12 dagar t.o.m. 19 okt". The period ends the day before the shift day,
 * which isn't payday (salary can land days later), so name the date instead.
 */
function DaysLeft({ period }: { period: Summary['period'] }) {
  return (
    <>
      i {period.daysLeft} {period.daysLeft === 1 ? 'dag' : 'dagar'} t.o.m.{' '}
      {formatShortDate(period.lastDate)}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="block px-1">
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      <span className="block text-sm font-semibold tabular-nums">{value}</span>
    </span>
  );
}
